import { useEffect, useState } from 'react';
import { fetchDocumentSizeBytes } from './documentStorageService';

const docUrl = (doc) => doc.combinedPdfUrl || doc.url || doc.compositionUrl;

/**
 * Size in bytes for each document in the list, keyed by the document's
 * download URL. Uses the persisted `sizeBytes` when present; entries saved
 * before it was recorded are looked up from Storage metadata once.
 *
 * @returns {Record<string, number|null>} url → bytes (null while loading / unknown)
 */
export default function useDocumentSizes(documents) {
  const [sizes, setSizes] = useState({});

  useEffect(() => {
    let cancelled = false;
    const missing = documents.filter(
      (d) =>
        typeof d.sizeBytes !== 'number' && docUrl(d) && !(docUrl(d) in sizes)
    );
    if (missing.length === 0) return undefined;

    Promise.all(
      missing.map(async (d) => [
        docUrl(d),
        await fetchDocumentSizeBytes(docUrl(d)),
      ])
    ).then((entries) => {
      if (cancelled) return;
      setSizes((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    });

    return () => {
      cancelled = true;
    };
    // `sizes` is intentionally omitted: it only grows with keys we just fetched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  return Object.fromEntries(
    documents.map((d) => [
      docUrl(d),
      typeof d.sizeBytes === 'number'
        ? d.sizeBytes
        : (sizes[docUrl(d)] ?? null),
    ])
  );
}
